<template>
    <div 
        v-if="user"
        class="favorite-heart-button tw-relative tw-cursor-pointer tw-select-none"
        :class="{ 'tw-opacity-50 tw-cursor-not-allowed': disabled || loading }"
        @click="handleToggle"
    >
        <!-- Main heart icon -->
        <q-icon
            :name="isFavorite ? 'fa-solid fa-heart' : 'fa-regular fa-heart'"
            :class="[
                'favorite-heart tw-transition-all tw-duration-300 tw-ease-out',
                isFavorite ? 'tw-text-red-500' : 'tw-text-gray-400 hover:tw-text-red-300',
                isAnimating && 'heart-bounce',
                getSizeClass(),
                loading && 'tw-animate-pulse'
            ]"
        />
        
        <!-- Floating heart particles -->
        <div
            v-if="showParticles"
            class="heart-particles tw-absolute tw-inset-0 tw-pointer-events-none tw-overflow-visible"
        >
            <div
                v-for="particle in particles"
                :key="particle.id"
                :class="[
                    'particle tw-absolute tw-text-red-400',
                    `particle-${particle.id}`
                ]"
                :style="particle.style"
            >
                <q-icon name="fa-solid fa-heart" size="xs" />
            </div>
        </div>
        
        <!-- Ripple effect -->
        <div
            v-if="showRipple"
            class="heart-ripple tw-absolute tw-inset-0 tw-rounded-full tw-border-2 tw-border-red-300 tw-pointer-events-none"
        ></div>
        
        <!-- Love burst effect -->
        <div
            v-if="showBurst"
            class="love-burst tw-absolute tw-inset-0 tw-pointer-events-none"
        >
            <div class="burst-ring tw-absolute tw-inset-0 tw-rounded-full tw-border tw-border-red-400"></div>
            <div class="burst-ring-2 tw-absolute tw-inset-0 tw-rounded-full tw-border tw-border-pink-300"></div>
        </div>
        
        <!-- Tooltip -->
        <q-tooltip v-if="showTooltip" :delay="500">
            {{ isFavorite ? 'Remove from favorites' : 'Add to favorites' }}
        </q-tooltip>
    </div>
</template>

<script setup lang="ts">
import { useSanctumUser } from '#imports';
import { useToggleFavoriteProducts } from '~/composables/toggleFavorite'
import type { User } from '~/models/User';
import type { Product } from '~/models/Product';

/**
 * Props and emits section
 */
const props = defineProps<{
    product?: Product
    initialFavorite?: boolean
    size?: 'small' | 'medium' | 'large' | 'xl'
    disabled?: boolean
    showTooltip?: boolean
}>()

const emits = defineEmits<{
    (e: 'favoriteChanged', value: boolean): void
    (e: 'error', error: any): void
}>()

/**
 * Ref, computed and hooks section
 */
const { toggleFavoriteProduct } = useToggleFavoriteProducts()
const user = useSanctumUser<User>()
const isFavorite = ref(props.product?.favorites_users_ids?.includes(user.value?.id ?? 0) ?? false)
const loading = ref(false)
const isAnimating = ref(false)
const showParticles = ref(false)
const showRipple = ref(false)
const showBurst = ref(false)
const particles = ref<Array<{
    id: number
    style: Record<string, string>
}>>([])

watch(() => props.product, () => {
    isFavorite.value = props.product?.favorites_users_ids?.includes(user.value?.id ?? 0) ?? false
},{
    immediate: true,
    deep: true
})

const getSizeClass = () => {
    switch (props.size) {
        case 'small': return 'tw-text-sm'
        case 'large': return 'tw-text-2xl'
        case 'xl': return 'tw-text-3xl'
        default: return 'tw-text-lg'
    }
}

/**
 * Functions section
 */
const handleToggle = async () => {
    if (props.disabled || loading.value) return
    
    loading.value = true
    const newFavoriteState = !isFavorite.value
    
    try {
        // Call the existing favorite toggle function
        await toggleFavoriteProduct(
            props.product?.id ?? 0, 
            newFavoriteState ? 'add' : 'remove'
        )
        
        // Update state and trigger animation
        isFavorite.value = newFavoriteState
        emits('favoriteChanged', newFavoriteState)
        
        if (newFavoriteState) {
            await triggerLoveAnimation()
        } else {
            await triggerUnfavoriteAnimation()
        }
        
    } catch (error) {
        console.error('Error toggling favorite:', error)
        emits('error', error)
    } finally {
        loading.value = false
    }
}

const triggerLoveAnimation = async () => {
    isAnimating.value = true
    
    // Create sequential animation effects
    setTimeout(() => createHeartParticles(), 100)
    setTimeout(() => showRipple.value = true, 200)
    setTimeout(() => showBurst.value = true, 300)
    
    // Reset all animations
    setTimeout(() => {
        isAnimating.value = false
        showParticles.value = false
        showRipple.value = false
        showBurst.value = false
        particles.value = []
    }, 2000)
}

const triggerUnfavoriteAnimation = async () => {
    isAnimating.value = true
    
    setTimeout(() => {
        isAnimating.value = false
    }, 400)
}

const createHeartParticles = () => {
    const particleCount = 12
    particles.value = []
    
    for (let i = 0; i < particleCount; i++) {
        const angle = (360 / particleCount) * i
        const distance = 30 + Math.random() * 25
        const duration = 1.2 + Math.random() * 0.8
        
        const x = Math.cos(angle * Math.PI / 180) * distance
        const y = Math.sin(angle * Math.PI / 180) * distance
        
        particles.value.push({
            id: i,
            style: {
                '--particle-x': `${x}px`,
                '--particle-y': `${y}px`,
                '--duration': `${duration}s`,
                '--delay': `${i * 0.05}s`,
                'animation-delay': `${i * 0.05}s`,
                'animation-duration': `${duration}s`
            }
        })
    }
    
    showParticles.value = true
}

// Expose methods for parent components
defineExpose({
    setFavorite: (value: boolean) => {
        isFavorite.value = value
    },
    getFavorite: () => isFavorite.value
})
</script>

<style scoped>
.favorite-heart-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    border-radius: 50%;
    transition: all 0.3s ease;
}

.favorite-heart-button:hover {
    background-color: rgba(239, 68, 68, 0.1);
}

.favorite-heart {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    transform-origin: center;
    transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.favorite-heart-button:hover .favorite-heart {
    transform: scale(1.15);
}

.heart-bounce {
    animation: heartBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.heart-ripple {
    animation: rippleExpand 1.2s ease-out;
    transform-origin: center;
}

.particle {
    animation: particleFloat var(--duration, 1.5s) ease-out forwards;
    font-size: 0.6rem;
    opacity: 0;
    z-index: 10;
}

.love-burst .burst-ring {
    animation: burstRing 1s ease-out;
}

.love-burst .burst-ring-2 {
    animation: burstRing 1s ease-out 0.2s;
}

/* Keyframe animations */
@keyframes heartBounce {
    0% { 
        transform: scale(1); 
    }
    20% { 
        transform: scale(1.4) rotate(-5deg); 
    }
    40% { 
        transform: scale(1.2) rotate(3deg); 
    }
    60% { 
        transform: scale(1.3) rotate(-2deg); 
    }
    80% { 
        transform: scale(1.1) rotate(1deg); 
    }
    100% { 
        transform: scale(1) rotate(0deg); 
    }
}

@keyframes rippleExpand {
    0% {
        transform: scale(0);
        opacity: 0.8;
    }
    50% {
        opacity: 0.4;
    }
    100% {
        transform: scale(4);
        opacity: 0;
    }
}

@keyframes particleFloat {
    0% {
        transform: translate(0, 0) scale(0) rotate(0deg);
        opacity: 1;
    }
    25% {
        opacity: 1;
        transform: translate(calc(var(--particle-x) * 0.3), calc(var(--particle-y) * 0.3)) scale(1) rotate(180deg);
    }
    50% {
        opacity: 0.8;
        transform: translate(calc(var(--particle-x) * 0.7), calc(var(--particle-y) * 0.7)) scale(1.1) rotate(270deg);
    }
    100% {
        transform: translate(var(--particle-x), var(--particle-y)) scale(0) rotate(360deg);
        opacity: 0;
    }
}

@keyframes burstRing {
    0% {
        transform: scale(0);
        opacity: 1;
    }
    100% {
        transform: scale(3);
        opacity: 0;
    }
}

/* Particle color variations */
.particle-0, .particle-6 { color: #ef4444; }
.particle-1, .particle-7 { color: #f87171; }
.particle-2, .particle-8 { color: #fca5a5; }
.particle-3, .particle-9 { color: #dc2626; }
.particle-4, .particle-10 { color: #f472b6; }
.particle-5, .particle-11 { color: #ec4899; }
</style> 